import React, { useState, useRef } from 'react';
import { apiFetch, apiDownload } from '../../../../lib/api';
import { API_DOCS } from './TabDocumenti.api';
import ApiInspector from '../../../../components/ApiInspector';

// ── Icons ─────────────────────────────────────────────────────────────────
const UploadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="17 8 12 3 7 8"></polyline>
    <line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const FileIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
    <polyline points="13 2 13 9 20 9"></polyline>
  </svg>
);

// ── Interfaces ────────────────────────────────────────────────────────────
interface Documento {
  id: number;
  fileName: string;
  fileUrl: string;
  fileMimeType?: string;
  fileSize?: number;
  etichetta?: string;
  uploadedAt: string;
}

interface TabDocumentiProps {
  nominativoId: number;
  documenti: Documento[];
  onReload: () => void;
}

// ── Helper: file icon CSS class ───────────────────────────────────────────
function getFileIconClass(mimeType?: string): string {
  if (!mimeType) return 'bop-tab-documenti__file-icon--default';
  if (mimeType.startsWith('image/')) return 'bop-tab-documenti__file-icon--image';
  if (mimeType.includes('pdf')) return 'bop-tab-documenti__file-icon--pdf';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'bop-tab-documenti__file-icon--word';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'bop-tab-documenti__file-icon--excel';
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('compressed')) return 'bop-tab-documenti__file-icon--archive';
  return 'bop-tab-documenti__file-icon--default';
}

// ── Componente ────────────────────────────────────────────────────────────
export default function TabDocumenti({ nominativoId, documenti, onReload }: TabDocumentiProps) {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [etichetta, setEtichetta] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFileExtension = (fileName: string) => {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '?';
  };

  const openUploadModal = () => {
    setSelectedFile(null);
    setEtichetta('');
    setShowUploadModal(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 20 * 1024 * 1024) {
        alert('Il file supera i 20 MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Seleziona un file da caricare');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (etichetta) formData.append('etichetta', etichetta);

      // @api POST /api/nominativi/:id/documenti {"file":"File","etichetta":"string"} -> {"ok":"true","documento":"Documento"}
      await apiFetch(`/api/nominativi/${nominativoId}/documenti`, {
        method: 'POST',
        body: formData,
      });

      setShowUploadModal(false);
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: Documento) => {
    try {
      // @api GET /api/nominativi/:id/documenti/:docId/download -> file stream (attachment)
      await apiDownload(
        `/api/nominativi/${nominativoId}/documenti/${doc.id}/download`,
        doc.fileName || 'documento'
      );
    } catch (err: any) {
      alert(err.message || 'Errore durante il download');
    }
  };

  const handleDelete = async (doc: Documento) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il documento "${doc.fileName}"?`)) return;

    try {
      // @api DELETE /api/nominativi/:id/documenti/:docId -> {"ok":"true"}
      await apiFetch(`/api/nominativi/${nominativoId}/documenti/${doc.id}`, {
        method: 'DELETE',
      });
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante l\'eliminazione');
    }
  };

  return (
    <div className="bop-tab-documenti">
      <div className="bop-card">
        <h3 className="bop-tab-documenti__section-title">
          Documenti Allegati
          <button className="bop-btn-primary" onClick={openUploadModal} disabled={uploading}>
            <UploadIcon />
            Carica Documento
          </button>
        </h3>

        {documenti && documenti.length > 0 ? (
          <table className="bop-table">
            <thead>
              <tr>
                <th className="bop-tab-documenti__th-tipo">Tipo</th>
                <th>Nome File</th>
                <th>Etichetta</th>
                <th>Dimensione</th>
                <th>Caricato il</th>
                <th className="bop-text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {documenti.map((doc) => (
                <tr key={doc.id}>
                  <td>
                    <div className={`bop-tab-documenti__file-icon ${getFileIconClass(doc.fileMimeType)}`}>
                      {getFileExtension(doc.fileName)}
                    </div>
                  </td>
                  <td className="bop-tab-documenti__td-name">
                    {doc.fileName}
                  </td>
                  <td className="bop-tab-documenti__td-secondary">
                    {doc.etichetta || '—'}
                  </td>
                  <td className="bop-tab-documenti__td-secondary">
                    {formatFileSize(doc.fileSize)}
                  </td>
                  <td className="bop-tab-documenti__td-secondary">
                    {formatDate(doc.uploadedAt)}
                  </td>
                  <td className="bop-text-center">
                    <div className="bop-tab-documenti__actions">
                      <button
                        className="bop-btn-secondary"
                        onClick={() => handleDownload(doc)}
                        title="Scarica"
                      >
                        <DownloadIcon />
                      </button>
                      <button
                        className="bop-btn-danger"
                        onClick={() => handleDelete(doc)}
                        title="Elimina"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="bop-empty-state bop-tab-documenti__empty">
            <FileIcon />
            <p className="bop-tab-documenti__empty-title">
              Nessun documento allegato
            </p>
            <p className="bop-tab-documenti__empty-desc">
              Carica documenti come contratti, fatture, certificati o altri file relativi a questo nominativo.
            </p>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bop-card bop-tab-documenti__info-box">
        <h3 className="bop-tab-documenti__info-title">
          ℹ️ Informazioni
        </h3>
        <ul className="bop-tab-documenti__info-list">
          <li>Dimensione massima per file: <strong>20 MB</strong></li>
          <li>Formati supportati: PDF, immagini (JPG, PNG), documenti Office (Word, Excel), archivi (ZIP)</li>
          <li>I documenti vengono salvati su <strong>Google Cloud Storage</strong></li>
          <li>È possibile aggiungere un'etichetta descrittiva per ogni documento</li>
        </ul>
      </div>

      {/* Modal Upload */}
      {showUploadModal && (
        <div className="bop-modal-overlay" onClick={() => !uploading && setShowUploadModal(false)}>
          <div className="bop-tab-documenti__modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="bop-tab-documenti__modal-header">Carica Documento</h3>

            <div className="bop-form-group">
              <label className="bop-label">Etichetta (opzionale)</label>
              <input
                className="bop-input"
                value={etichetta}
                onChange={(e) => setEtichetta(e.target.value)}
                placeholder="es. Contratto, Fattura, Certificato..."
                disabled={uploading}
              />
            </div>

            <div className="bop-form-group">
              <label className="bop-label">File *</label>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="bop-tab-documenti__file-input-hidden"
                disabled={uploading}
              />
              <div
                className="bop-tab-documenti__dropzone"
                onClick={() => !uploading && fileInputRef.current?.click()}
              >
                {selectedFile ? (
                  <>
                    <div className="bop-tab-documenti__dropzone-icon">📄</div>
                    <div className="bop-tab-documenti__dropzone-filename">
                      {selectedFile.name}
                    </div>
                    <div className="bop-tab-documenti__dropzone-size">
                      {formatFileSize(selectedFile.size)}
                    </div>
                    {!uploading && (
                      <div
                        className="bop-tab-documenti__dropzone-change"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                        }}
                      >
                        Cambia file
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <UploadIcon />
                    <div className="bop-tab-documenti__dropzone-label">
                      Clicca per selezionare un file
                    </div>
                    <div className="bop-tab-documenti__dropzone-hint">
                      Massimo 20 MB
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="bop-tab-documenti__modal-actions">
              <button className="bop-btn-secondary" onClick={() => setShowUploadModal(false)} disabled={uploading}>
                Annulla
              </button>
              <button className="bop-btn-primary" onClick={handleUpload} disabled={uploading || !selectedFile}>
                {uploading ? 'Caricamento...' : 'Carica'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </div>
  );
}
